import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { uploadFile, errDetail } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const IMG = ["jpg", "jpeg", "png", "webp"];
const VID = ["mp4", "mov"];
const MB = 1024 * 1024;

export const useUploader = (imageOnly = false) => {
  const { t, te } = useLang();
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [error, setError] = useState("");

  const upload = async (file) => {
    if (!file) return null;
    setError("");
    const ext = file.name.split(".").pop().toLowerCase();
    let msg = "";
    if (!IMG.includes(ext) && (imageOnly || !VID.includes(ext))) msg = "Format non supporté";
    else if (IMG.includes(ext) && file.size > 20 * MB) msg = "Fichier trop volumineux (20 Mo max)";
    else if (VID.includes(ext) && file.size > 200 * MB) msg = "Fichier trop volumineux (200 Mo max)";
    if (msg) { setError(te(msg)); toast.error(te(msg)); return null; }
    setBusy(true);
    setPct(0);
    try {
      return await uploadFile(file, setPct);
    } catch (e) {
      const m = te(errDetail(e));
      setError(m);
      toast.error(m);
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { upload, busy, pct, error, label: busy ? `${t("uploading")} ${pct}%` : null };
};

export const UploadDrop = ({ onFile, testId, title, hint, imageOnly = false, compact = false }) => {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const { upload, busy, error, label } = useUploader(imageOnly);
  const handle = async (file) => {
    const f = await upload(file);
    if (ref.current) ref.current.value = "";
    if (f) onFile(f);
  };
  return (
    <div className="frz-upload">
      <button type="button" data-testid={`${testId}-dropzone`} disabled={busy}
        className={`frz-drop ${compact ? "is-compact" : ""} ${over ? "is-over" : ""}`}
        onClick={() => ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); handle(e.dataTransfer.files?.[0]); }}>
        {busy ? <Loader2 className="spin" size={compact ? 18 : 28} /> : <Upload size={compact ? 18 : 28} />}
        <span className="frz-drop-title">{label || title}</span>
        {!compact && hint && <span className="frz-drop-hint">{hint}</span>}
      </button>
      <input ref={ref} type="file" hidden data-testid={`${testId}-input`}
        accept={imageOnly ? ".jpg,.jpeg,.png,.webp" : ".jpg,.jpeg,.png,.webp,.mp4,.mov"}
        onChange={(e) => handle(e.target.files?.[0])} />
      {error && <p className="frz-error" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
};
